import React, { useState } from "react";
import { X } from "lucide-react";
import { CARS, QUESTS, OUTFITS, SHOPS, HOME_PRICES, DECOR, HOMES, AIRCRAFT, CAFE_MENU, PETS, JOBS } from "./cityData";
import { STATUSES } from "./cityStats";
import { sfx } from "./citySfx";

export function MiniModal({ title, onClose, children, testid, wide }) {
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/85 backdrop-blur-sm p-3" role="dialog" aria-modal="true" data-testid={testid}>
      <div className={`relative w-full ${wide ? "max-w-6xl" : "max-w-3xl"} max-h-[95vh] overflow-y-auto border-2 border-cyan-400/60 bg-[#070a1c] p-4 sm:p-6`}>
        <button onClick={onClose} className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center" aria-label="Close" data-testid="mini-close"><X size={16} /></button>
        <p className="cq-px text-cyan-300 text-[10px] sm:text-sm mb-4">{title}</p>
        {children}
      </div>
    </div>
  );
}

const Btn = ({ children, onClick, tone = "cyan", disabled, testid }) => (
  <button disabled={disabled} onClick={onClick} data-testid={testid} className={`cq-px text-[8px] sm:text-[10px] px-4 py-2.5 disabled:opacity-40 ${tone === "amber" ? "border border-amber-400 text-amber-300 hover:bg-amber-400/10" : "bg-cyan-400 text-slate-950 hover:bg-cyan-300"}`}>{children}</button>
);

function aprFor(score) { return score >= 780 ? 5.9 : score >= 720 ? 7.4 : score >= 660 ? 10.9 : score >= 600 ? 15.9 : 21.9; }

export function AutoSim({ coins, owned, current, onBuy, onDrive, stats, onFinance }) {
  const [score, setScore] = useState(stats?.fundingiq ? Math.min(850, 500 + Math.round(stats.fundingiq * 0.35)) : 680);
  const [price, setPrice] = useState(28000);
  const [down, setDown] = useState(10);
  const [term, setTerm] = useState(60);
  const apr = aprFor(score), P = price * (1 - down / 100), r = apr / 100 / 12;
  const pay = r ? (P * r) / (1 - Math.pow(1 + r, -term)) : P / term;
  const total = pay * term, interest = total - P;
  const Slider = ({ label, v, set, min, max, step, fmt, testid }) => (
    <label className="block"><span className="cq-px text-[7px] text-slate-400">{label}: <span className="text-amber-300">{fmt(v)}</span></span>
      <input type="range" min={min} max={max} step={step} value={v} onChange={(e) => set(Number(e.target.value))} className="w-full accent-cyan-400" data-testid={testid} /></label>
  );
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="space-y-3" data-testid="auto-sim">
        <Slider label="Credit score" v={score} set={setScore} min={500} max={850} step={5} fmt={(x) => x} testid="sim-score" />
        <Slider label="Car price" v={price} set={setPrice} min={5000} max={80000} step={500} fmt={(x) => `$${x.toLocaleString()}`} testid="sim-price" />
        <Slider label="Down payment" v={down} set={setDown} min={0} max={50} step={5} fmt={(x) => `${x}%`} testid="sim-down" />
        <Slider label="Term" v={term} set={setTerm} min={24} max={84} step={12} fmt={(x) => `${x} months`} testid="sim-term" />
        <div className="border border-cyan-400/30 bg-cyan-400/5 p-3 grid grid-cols-2 gap-2">
          <div><p className="cq-px text-[7px] text-slate-400">APR</p><p className="cq-px text-[11px] text-amber-300" data-testid="sim-apr">{apr}%</p></div>
          <div><p className="cq-px text-[7px] text-slate-400">Monthly</p><p className="cq-px text-[11px] text-emerald-300" data-testid="sim-payment">${Math.round(pay).toLocaleString()}</p></div>
          <div><p className="cq-px text-[7px] text-slate-400">Total interest</p><p className="cq-px text-[9px] text-red-300">${Math.round(interest).toLocaleString()}</p></div>
          <div><p className="cq-px text-[7px] text-slate-400">vs 780 score</p><p className="cq-px text-[9px] text-slate-200">{score >= 780 ? "best tier ✓" : `saves $${Math.max(0, Math.round(interest - ((P * (5.9 / 1200)) / (1 - Math.pow(1 + 5.9 / 1200, -term)) * term - P))).toLocaleString()}`}</p></div>
        </div>
        <p className="font-mono text-[11px] text-slate-400">Lesson: a 100-point jump usually cuts APR in half. Shorter terms cost more per month but far less overall.</p>
      </div>
      <div className="space-y-2" data-testid="auto-garage">
        <p className="cq-px text-[8px] text-slate-300">GARAGE · you have <span className="text-amber-300">${coins}</span> coins</p>
        {CARS.map((c) => {
          const has = owned.includes(c.id);
          return (
            <div key={c.id} className="flex items-center gap-3 border border-slate-700 p-2" data-testid={`car-${c.id}`}>
              <img src={c.img} alt={c.name} className="h-9 w-auto" style={c.hue != null ? { filter: c.dark ? "brightness(0.35) saturate(0.3)" : `hue-rotate(${c.hue}deg)` } : undefined} />
              <div className="flex-1 min-w-0"><p className="cq-px text-[8px] text-white">{c.name}</p><p className="cq-px text-[7px] text-slate-400">{c.cost} coins · MSRP ${c.msrp.toLocaleString()} · speed x{c.speed}</p></div>
              {has ? <Btn tone={current === c.id ? "cyan" : "amber"} onClick={() => onDrive(current === c.id ? null : c.id)} testid={`car-drive-${c.id}`}>{current === c.id ? "PARK" : "DRIVE"}</Btn>
                : <><Btn disabled={coins < c.cost} onClick={() => onBuy(c)} testid={`car-buy-${c.id}`}>BUY</Btn>{onFinance && <Btn tone="amber" onClick={() => onFinance(c)} testid={`car-finance-${c.id}`}>FINANCE</Btn>}</>}
            </div>
          );
        })}
        <p className="font-mono text-[11px] text-slate-400">Earn coins on the street, in back rooms, quizzes and the dojo. Cars persist on this device.</p>
      </div>
    </div>
  );
}

const QUIZ = [
  { q: "What share of your FICO score is payment history?", a: ["10%", "35%", "50%"], c: 1 },
  { q: "Ideal credit utilization for the best tier?", a: ["Under 10%", "Around 50%", "Doesn't matter"], c: 0 },
  { q: "How long does a hard inquiry affect your score?", a: ["7 years", "About 1 year", "Forever"], c: 1 },
  { q: "Bureaus must verify a disputed item within…", a: ["30 days", "90 days", "1 year"], c: 0 },
  { q: "Closing your oldest card usually…", a: ["Helps your score", "Hurts your score", "Does nothing"], c: 1 },
];
export function Quiz({ onCoin, onPass }) {
  const [i, setI] = useState(0); const [picked, setPicked] = useState(null); const [right, setRight] = useState(0); const [done, setDone] = useState(false);
  const q = QUIZ[i];
  const pick = (k) => { if (picked != null) return; setPicked(k); if (k === q.c) { setRight((r) => r + 1); onCoin(2); sfx.coin(); } else sfx.hit(); };
  const next = () => { if (i + 1 >= QUIZ.length) { setDone(true); if (right >= 4) onPass(); } else { setI(i + 1); setPicked(null); } };
  if (done) return <div className="text-center space-y-3" data-testid="quiz-done"><p className="cq-px text-amber-300 text-sm">{right}/{QUIZ.length}</p><p className="font-mono text-slate-200 text-sm">{right >= 4 ? "Honor roll. +coins banked and quest complete." : "Not bad. Grandma Penny has tips on the street — try again after."}</p></div>;
  return (
    <div className="space-y-3" data-testid="quiz">
      <p className="cq-px text-[7px] text-slate-400">QUESTION {i + 1}/{QUIZ.length} · +2 coins each</p>
      <p className="font-mono text-sm text-white">{q.q}</p>
      <div className="grid gap-2">{q.a.map((a, k) => <button key={a} onClick={() => pick(k)} data-testid={`quiz-opt-${k}`} className={`cq-px text-[8px] sm:text-[9px] text-left px-4 py-3 border ${picked == null ? "border-slate-600 text-slate-100 hover:border-cyan-400" : k === q.c ? "border-emerald-400 text-emerald-300" : k === picked ? "border-red-400 text-red-300" : "border-slate-700 text-slate-500"}`}>{a}</button>)}</div>
      {picked != null && <Btn onClick={next} testid="quiz-next">{i + 1 >= QUIZ.length ? "FINISH" : "NEXT"}</Btn>}
    </div>
  );
}

const EVIDENCE = ["Bank statement showing the payment posted on time", "Bureau report listing the account twice", "Letter from furnisher with a different balance", "Your ID + proof of address"];
export function CourtCase({ onWin }) {
  const [got, setGot] = useState([]); const [verdict, setVerdict] = useState(null);
  const toggle = (i) => setGot((g) => (g.includes(i) ? g.filter((x) => x !== i) : [...g, i]));
  const file = () => { const ok = got.length >= 3; setVerdict(ok ? "VERDICT: DELETED. The furnisher could not verify within 30 days. Item removed from all three bureaus." : "VERDICT: VERIFIED. Not enough evidence — collect at least 3 documents and refile."); if (ok) { sfx.win(); onWin(); } else sfx.ko(); };
  return (
    <div className="space-y-3" data-testid="court-case">
      <p className="font-mono text-sm text-slate-200">Case #4471: a collection reported with the wrong balance. Collect your evidence, then file.</p>
      {EVIDENCE.map((e, i) => <button key={e} onClick={() => toggle(i)} data-testid={`evidence-${i}`} className={`w-full text-left cq-px text-[8px] px-3 py-2.5 border ${got.includes(i) ? "border-emerald-400 text-emerald-300" : "border-slate-600 text-slate-200"}`}>{got.includes(i) ? "✓" : "○"} {e}</button>)}
      <Btn onClick={file} testid="court-file">FILE THE CASE ({got.length}/4)</Btn>
      {verdict && <p className="cq-px text-[8px] text-amber-300 leading-relaxed" data-testid="court-verdict">{verdict}</p>}
    </div>
  );
}

export function CasinoOdds() {
  const [chips, setChips] = useState(100); const [log, setLog] = useState([]); const [spins, setSpins] = useState(0);
  const spin = (bet) => {
    const n = Math.floor(Math.random() * 38); const color = n === 0 || n === 37 ? "green" : n % 2 ? "red" : "black";
    const win = bet === color; const delta = win ? (bet === "green" ? 170 : 10) : -10;
    setChips((c) => c + delta); setSpins((s) => s + 1); setLog((l) => [`${win ? "WIN" : "LOSS"} · landed ${color} · ${delta > 0 ? "+" : ""}${delta}`, ...l].slice(0, 5));
    win ? sfx.coin() : sfx.hit();
  };
  return (
    <div className="space-y-3" data-testid="casino">
      <p className="cq-px text-[9px] text-amber-300">FAKE CHIPS: {chips} · spins: {spins}</p>
      <div className="flex gap-2">{["red", "black", "green"].map((c) => <Btn key={c} tone="amber" onClick={() => spin(c)} testid={`bet-${c}`}>BET 10 ON {c.toUpperCase()}</Btn>)}</div>
      <div className="font-mono text-[12px] text-slate-300 space-y-1">{log.map((l, i) => <p key={i}>{l}</p>)}</div>
      <div className="border border-cyan-400/30 bg-cyan-400/5 p-3 font-mono text-[12px] text-slate-200 leading-relaxed">
        Red/black pays 1:1 but only hits 18/38 = 47.4%. Expected value: −5.3% per spin. A payday loan at 400% APR is the same math, every two weeks, without the free drinks. Responsible borrowing = only bet what the math says you'll keep.
      </div>
    </div>
  );
}

export function DreamHomes({ stats, user }) {
  const stage = stats?.stageIndex ?? -1;
  return (
    <div className="grid sm:grid-cols-3 gap-3" data-testid="dream-homes">
      {HOMES.map((h) => { const open = stage >= h.stage; return (
        <div key={h.name} className={`border p-3 ${open ? "border-emerald-400/60" : "border-slate-700 opacity-80"}`} data-testid={`home-${h.stage}`}>
          <p className="cq-px text-[9px] text-white">{open ? "🔓" : "🔒"} {h.name}</p>
          <p className="cq-px text-[7px] text-slate-400 mt-2">Unlocks at stage {h.stage + 1}: {STATUSES[h.stage]}</p>
          <p className="font-mono text-[11px] text-slate-300 mt-2">{open ? h.perk : user ? `You're at stage ${stage + 1}. Keep going.` : "Sign up and your real tracker stage unlocks homes."}</p>
        </div>); })}
    </div>
  );
}

const DESTS = [["Duckburg", 0], ["Miami", 1], ["Cancún", 2], ["Lisbon", 3], ["Tokyo", 4], ["Maldives", 6]];
export function Airport({ questsDone }) {
  return (
    <div className="space-y-3" data-testid="airport-board">
      <p className="cq-px text-[8px] text-slate-400">ACHIEVEMENTS UNLOCKED: {questsDone}/{QUESTS.length}</p>
      <div className="grid sm:grid-cols-2 gap-2">{DESTS.map(([d, need]) => { const ok = questsDone >= need; return <div key={d} className={`flex justify-between border px-3 py-2 cq-px text-[8px] ${ok ? "border-emerald-400/60 text-emerald-300" : "border-slate-700 text-slate-500"}`} data-testid={`dest-${d}`}><span>{d}</span><span>{ok ? "BOARDING" : `${need} quests`}</span></div>; })}</div>
      <p className="font-mono text-[12px] text-slate-300 leading-relaxed">Points 101: a 60,000-point sign-up bonus ≈ $600–$900 in flights. Earn it by hitting the minimum spend with money you'd spend anyway, then pay it off in full. Never chase points with debt.</p>
    </div>
  );
}

export function Shop({ shop, coins, owned, outfit, onBuy, onWear, sale }) {
  const items = OUTFITS.filter((i) => i.shop === shop);
  return (
    <div className="space-y-3" data-testid={`shop-${shop}`}>
      <p className="cq-px text-[8px] text-slate-300">{SHOPS[shop].name} · you have <span className="text-amber-300">${coins}</span>{sale && <span className="text-pink-400"> · FLASH SALE 50% OFF</span>}</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {items.map((it) => { const has = owned.includes(it.id); const worn = outfit[it.slot] === it.style; const cost = sale ? Math.ceil(it.cost / 2) : it.cost; return (
          <div key={it.id} className="flex items-center justify-between gap-2 border border-slate-700 p-2" data-testid={`item-${it.id}`}>
            <div><p className="cq-px text-[8px] text-white">{it.name}</p><p className="cq-px text-[7px] text-slate-400">{it.slot} · {cost} coins</p></div>
            {has ? <Btn tone={worn ? "cyan" : "amber"} onClick={() => onWear(it, !worn)} testid={`wear-${it.id}`}>{worn ? "REMOVE" : "WEAR"}</Btn> : <Btn disabled={coins < cost} onClick={() => onBuy(it, cost)} testid={`buy-${it.id}`}>BUY</Btn>}
          </div>); })}
      </div>
      <p className="font-mono text-[11px] text-slate-400">Other shops: {Object.entries(SHOPS).filter(([k]) => k !== shop).map(([, v]) => `${v.name} (${v.where})`).join(" · ")}</p>
    </div>
  );
}

export function Board({ data }) {
  const cols = [["coins", "COINS"], ["quiz", "QUIZ PASSES"], ["ko", "DOJO KOs"], ["sky", "SKY COURSE · BEST"], ["derby", "HR DERBY · WEEK"]];
  return (
    <div className="grid sm:grid-cols-5 gap-3" data-testid="city-board">
      {cols.map(([k, t]) => (
        <div key={k} className="border border-amber-400/40 p-3">
          <p className="cq-px text-[8px] text-amber-300 mb-2">{t}</p>
          {(data?.[k] || []).length === 0 && <p className="cq-px text-[7px] text-slate-500">no entries yet</p>}
          {(data?.[k] || []).map((r, i) => <div key={i} className="flex justify-between cq-px text-[8px] py-1 text-slate-100" data-testid={`board-${k}-${i}`}><span>{i + 1}. {r.name}{r.client ? " ★" : ""}</span><span className="text-emerald-300">{r.value}</span></div>)}
        </div>
      ))}
    </div>
  );
}

export function CafeMenu({ coins, onBuy, active = {} }) {
  return (
    <div className="space-y-2" data-testid="cafe-menu">
      <p className="cq-px text-[8px] text-slate-300">KICKOFF CAFÉ · MENU · you have <span className="text-amber-300">${coins}</span></p>
      {CAFE_MENU.map((m) => { const on = active[m.effect] && active[m.effect] > Date.now(); return (
        <div key={m.id} className="flex items-center gap-3 border border-slate-700 p-2" data-testid={`cafe-${m.id}`}>
          <div className="flex-1"><p className="cq-px text-[8px] text-white">{m.name}</p><p className="cq-px text-[7px] text-slate-400">{m.cost} coins · {m.desc}{on ? " · ACTIVE" : ""}</p></div>
          <Btn disabled={coins < m.cost} tone="amber" onClick={() => onBuy(m)} testid={`cafe-buy-${m.id}`}>BUY</Btn>
        </div>); })}
      <p className="font-mono text-[11px] text-slate-400">You carry what you buy — the perk runs while the timer lasts. Some jobs need a fresh espresso.</p>
    </div>
  );
}

export function PetShop({ coins, owned, current, onBuy, onWalk }) {
  return (
    <div className="space-y-2" data-testid="pet-shop">
      <p className="cq-px text-[8px] text-slate-300">PAWS & CLAWS · you have <span className="text-amber-300">${coins}</span></p>
      {PETS.filter((p) => !p.where).map((p) => { const has = owned.includes(p.id); return (
        <div key={p.id} className="flex items-center gap-3 border border-slate-700 p-2" data-testid={`pet-${p.id}`}>
          <img src={`/game/pet_${p.id}_0.webp`} alt={p.name} className="h-10 w-16 object-contain" style={{ imageRendering: "pixelated" }} />
          <div className="flex-1"><p className="cq-px text-[8px] text-white">{p.name}</p><p className="cq-px text-[7px] text-slate-400">{p.cost} coins · walks on a leash</p></div>
          {has ? <Btn tone={current === p.id ? "cyan" : "amber"} onClick={() => onWalk(current === p.id ? null : p.id)} testid={`pet-walk-${p.id}`}>{current === p.id ? "LEAVE HOME" : "WALK"}</Btn>
            : <Btn disabled={coins < p.cost} onClick={() => onBuy(p)} testid={`pet-buy-${p.id}`}>ADOPT</Btn>}
        </div>); })}
    </div>
  );
}

export function JobsBoard({ job, done = {}, onAccept, onAbandon }) {
  return (
    <div className="space-y-2" data-testid="jobs-board">
      <p className="cq-px text-[8px] text-slate-300">CITY JOBS BOARD · errands paid in coins</p>
      {JOBS.map((j) => { const active = job?.id === j.id; return (
        <div key={j.id} className={`border p-2 ${active ? "border-amber-400/70 bg-amber-400/5" : "border-slate-700"}`} data-testid={`job-${j.id}`}>
          <div className="flex items-center gap-2"><p className="cq-px text-[8px] text-white flex-1">{j.title}</p><span className="cq-px text-[7px] text-amber-300">+{j.pay}c{done[j.id] ? ` · done ×${done[j.id]}` : ""}</span></div>
          <ol className="mt-1.5 space-y-0.5">{j.steps.map((st, i) => <li key={i} className={`font-mono text-[11px] ${active && job.step === i ? "text-amber-200" : active && job.step > i ? "text-emerald-300 line-through" : "text-slate-400"}`}>{i + 1}. {st}</li>)}</ol>
          <div className="mt-2">{active ? <Btn onClick={onAbandon} testid={`job-abandon-${j.id}`}>ABANDON</Btn> : <Btn tone="amber" disabled={!!job} onClick={() => onAccept(j)} testid={`job-accept-${j.id}`}>ACCEPT</Btn>}</div>
        </div>); })}
    </div>
  );
}

export function Hangar({ coins, owned, current, onBuy, onFly, onFinance }) {
  return (
    <div className="space-y-2" data-testid="hangar-shop">
      <p className="cq-px text-[8px] text-slate-300">GATE 7 · PRIVATE AVIATION · you have <span className="text-amber-300">${coins}</span> coins</p>
      {AIRCRAFT.map((a) => { const has = owned.includes(a.id); return (
        <div key={a.id} className="flex items-center gap-3 border border-slate-700 p-2" data-testid={`air-${a.id}`}>
          <img src={a.img} alt={a.name} className="h-10 w-24 object-contain" />
          <div className="flex-1 min-w-0"><p className="cq-px text-[8px] text-white">{a.name}</p><p className="cq-px text-[7px] text-slate-400">{a.cost} coins · ${a.msrp.toLocaleString()} · speed x{a.speed}</p></div>
          {has ? <Btn tone={current === a.id ? "cyan" : "amber"} onClick={() => onFly(current === a.id ? null : a.id)} testid={`air-fly-${a.id}`}>{current === a.id ? "LAND & EXIT" : "FLY"}</Btn>
            : <><Btn disabled={coins < a.cost} onClick={() => onBuy(a)} testid={`air-buy-${a.id}`}>BUY</Btn>{onFinance && <Btn tone="amber" onClick={() => onFinance(a)} testid={`air-finance-${a.id}`}>FINANCE</Btn>}</>}
        </div>); })}
      <p className="font-mono text-[11px] text-slate-400">Fly: ↑/W climb, ↓/S dive, ←/→ cruise. Land (altitude 0) to enter buildings. Mobile: the D-pad gets ▲ ▼ while you're in the cockpit.</p>
    </div>
  );
}

export function HomesShop({ stats, coins, homes, decor, onBuyHome, onBuyDecor, onEnterHome }) {
  const stage = stats?.stageIndex ?? -1;
  return (
    <div className="space-y-4" data-testid="homes-shop">
      <div className="grid sm:grid-cols-3 gap-3">
        {HOMES.map((h, i) => { const owned = homes.includes(i); const unlocked = stage >= h.stage; return (
          <div key={h.name} className={`border p-3 ${owned ? "border-emerald-400/60" : "border-slate-700"}`} data-testid={`home-${h.stage}`}>
            <p className="cq-px text-[9px] text-white">{owned ? "🏠" : unlocked ? "🔓" : "🔒"} {h.name}</p>
            <p className="cq-px text-[7px] text-slate-400 mt-2">{HOME_PRICES[i]} coins · or free at stage {h.stage + 1}</p>
            {!owned && <div className="mt-2"><Btn disabled={!unlocked && coins < HOME_PRICES[i]} onClick={() => onBuyHome(i, unlocked ? 0 : HOME_PRICES[i])} testid={`buy-home-${i}`}>{unlocked ? "CLAIM" : "BUY"}</Btn></div>}
            {owned && <p className="font-mono text-[11px] text-emerald-300 mt-2">{h.perk}</p>}
            {owned && <div className="mt-2"><Btn tone="amber" onClick={() => onEnterHome(i)} testid={`enter-home-${i}`}>ENTER HOME</Btn></div>}
          </div>); })}
      </div>
      {homes.length > 0 && (
        <div>
          <p className="cq-px text-[8px] text-slate-300 mb-2">DECORATE · {decor.length}/{DECOR.length} items · placed inside every home you own</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {DECOR.map((d) => { const has = decor.includes(d.id); return <div key={d.id} className="flex justify-between items-center border border-slate-700 p-2" data-testid={`decor-${d.id}`}><span className="cq-px text-[8px] text-white">{has ? "✓ " : ""}{d.name}</span>{has ? <span className="cq-px text-[7px] text-emerald-300">PLACED</span> : <Btn disabled={coins < d.cost} onClick={() => onBuyDecor(d)} testid={`buy-decor-${d.id}`}>{d.cost}c</Btn>}</div>; })}
          </div>
        </div>
      )}
    </div>
  );
}
